import axios, { AxiosInstance } from "axios";
import logger from "../utils/logger";
import { ApiResponse } from "../types";
import { Constants } from "../utils/constants";
import {
  V2CountriesResponse,
  V2SignupRequest,
  V2SignupResponse,
  V2IndividualSubscriberSignupRequest,
  V2InviteUserPayload,
  V2OrganizationRole,
  V2Permission,
  V2OrganizationRolesResponse,
  V2UserSearchResponse,
  V2SelfUserResponse,
  V2UpdateSelfUserRequest,
  V2OrganizationTypesResponse,
  V2CreateClientOrganizationRequest,
  V2UpdateClientOrganizationRequest,
  V2RefreshTokenResponse,
  V2ForgotPasswordRequest,
  V2ResetPasswordRequest,
  V2ResetPasswordResponse,
} from "../types/v2types";

class ApiV2Service {
  private api: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    // Create authenticated API instance for V2
    this.api = axios.create({
      baseURL: `${Constants.USER_API_BASE_URL}/`,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token interceptor
    this.api.interceptors.request.use(config => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor with refresh token logic
    this.api.interceptors.response.use(
      response => ({
        data: response.data,
        status: response.status,
        message: response.data?.message,
      }),
      async error => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, add to queue
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.api(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Attempt to refresh token
            const refreshToken = localStorage.getItem("refresh_token");
            if (!refreshToken) {
              throw new Error("No refresh token available");
            }

            const response = await this.refreshToken(refreshToken);

            // Update tokens in localStorage
            localStorage.setItem("access_token", response.access_token);
            localStorage.setItem("refresh_token", response.refresh_token);

            // Update the failed queue
            this.processQueue(null, response.access_token);

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            this.processQueue(refreshError, null);
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("authToken");
            localStorage.removeItem("currentUser");

            // Redirect to signin page only if not on public pages
            const publicPages = [
              "/home",
              "/signin",
              "/auth/signin",
              "/signup",
              "/host-experience",
              "/report-issue",
              "/corporate-page",
              "/terms-of-use",
              "/privacy-policy",
            ];
            if (!publicPages.includes(window.location.pathname)) {
              window.location.href = "/signin";
            }
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        logger.error("V2 API Error:", error);
        throw error;
      }
    );
  }

  /**
   * Process the queue of failed requests
   */
  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  /**
   * Refresh access token using refresh token
   * @param refreshToken The refresh token to use
   * @returns Promise with new access and refresh tokens
   */
  public async refreshToken(refreshToken: string): Promise<V2RefreshTokenResponse> {
    try {
      const response = await axios.post(
        `${Constants.USER_API_BASE_URL}/auth/v1/token/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      logger.log("Token refresh successful");
      return response.data;
    } catch (error) {
      logger.error("Token refresh failed:", error);
      throw error;
    }
  }

  public async confirmEmail(token: string) {
    try {
      const response = await axios.post(
        `${Constants.USER_API_BASE_URL}/auth/v1/confirm/email`,
        { token },
        { headers: { "Content-Type": "application/json" } }
      );
      return response;
    } catch (error) {
      logger.error("Email confirmation failed:", error);
      throw error;
    }
  }

  public async confirmInvite(token: string, password: string) {
    try {
      const response = await axios.post(
        `${Constants.USER_API_BASE_URL}/auth/v1/confirm`,
        {
          token,
          password,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      logger.error("V2 Confirm Invite Error:", error);
      throw error;
    }
  }

  public async loginV2(username: string, password: string) {
    try {
      const response = await axios.post(
        `${Constants.USER_API_BASE_URL}/auth/v1/login`,
        {
          username,
          password,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      logger.error("V2 Login Error:", error);
      throw error;
    }
  }

  public async resendAccountConfirmation(payload: { username: string; confirmationUrl: string }) {
    try {
      const response = await axios.post(`${Constants.USER_API_BASE_URL}/auth/v1/account-confirmation/resend`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      return response;
    } catch (error) {
      logger.error("V2 Resend Account Confirmation Error:", error);
      throw error;
    }
  }

  /**
   * Resend email confirmation notification
   * @param username The username (email) to resend confirmation for
   */
  public async resendEmailConfirmation(username: string): Promise<ApiResponse<void>> {
    try {
      const response = await axios.post(
        `${Constants.USER_API_BASE_URL}/auth/v1/email-confirmation/resend`,
        { username },
        { headers: { "Content-Type": "application/json" } }
      );
      return {
        data: response.data,
        status: response.status,
        message: "Confirmation email sent successfully",
      };
    } catch (error) {
      logger.error("V2 Resend Email Confirmation Error:", error);
      throw error;
    }
  }

  /**
   * Reactivate a disabled user account
   * @param userId User ID
   * @param confirmationUrl Confirmation URL for the user
   */
  public async reactivateUser(userId: string, payload: { confirmationUrl: string }) {
    try {
      const response = await this.api.post(`/api/v1/users/${userId}/reactivation`, payload);
      logger.log("V2 Reactivate User Response:", response);
      return response;
    } catch (error) {
      logger.error("V2 Reactivate User Error:", error);
      throw error;
    }
  }

  /**
   * Get users by status (active, disabled, inactive)
   * @param status User status (active, disabled, inactive)
   * @param page Page number (optional)
   * @param limit Page size (optional)
   */
  public async getUsersByStatus(status: string, page = 0, limit = 10) {
    try {
      const params = new URLSearchParams();
      params.append("status", status);
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      const response = await this.api.get(`/api/v1/users?${params.toString()}`);
      logger.log("V2 Users By Status Response:", response);
      return response;
    } catch (error) {
      logger.error("V2 Get Users By Status Error:", error);
      throw error;
    }
  }

  /**
   * Invite a user using the V2 endpoint
   * @param payload V2InviteUserPayload
   */
  public async inviteUserV2(payload: V2InviteUserPayload) {
    try {
      const response = await this.api.post("/api/v1/users", payload);
      logger.log("V2 Invite User Response:", response);
      return response;
    } catch (error) {
      logger.error("V2 Invite User Error:", error);
      throw error;
    }
  }

  /**
   * Add host individual customer using dedicated endpoint
   * @param organizationId Host Organization ID
   * @param payload Individual customer payload
   */
  public async addHostIndividualCustomer(
    organizationId: number,
    payload: {
      username: string;
      firstName: string;
      lastName: string;
      emailAddress: string;
      msisdn: string;
      confirmationUrl: string;
    }
  ) {
    try {
      const response = await this.api.post(`/api/v1/users/hosts/${organizationId}/individual-customers`, payload);
      logger.log("V2 Add Host Individual Customer Response:", response);
      return response;
    } catch (error) {
      logger.error("V2 Add Host Individual Customer Error:", error);
      throw error;
    }
  }

  /**
   * Search for users
   */
  public async searchUsers(params: {
    q: string;
    isOwner?: boolean;
    page?: number;
    limit?: number;
    organizationId?: number;
    mode?: "ALL" | "CLIENTS" | "PARENT" | "SELF";
    status?: string;
    orderBy?: string;
    parentId?: number;
  }): Promise<V2UserSearchResponse> {
    try {
      const response = await this.api.get("/api/v1/users/search", { params });
      return response.data as V2UserSearchResponse;
    } catch (error) {
      logger.error("V2 Search Users Error:", error);
      throw error;
    }
  }

  /**
   * Update an existing user
   * @param userId User ID to update
   * @param payload User update data
   */
  public async updateUser(
    userId: string,
    payload: {
      organizationRoleId?: string;
      firstName?: string;
      lastName?: string;
      emailAddress?: string;
      msisdn?: string;
      imageUrl?: string;
      status?: string;
    }
  ) {
    try {
      const response = await this.api.patch(`/api/v1/users/${userId}`, payload);
      logger.log("V2 Update User Response:", response);
      return response;
    } catch (error) {
      logger.error("V2 Update User Error:", error);
      throw error;
    }
  }

  /**
   * Get organization role by ID
   */
  public async getOrganizationRoleById(id: string): Promise<V2OrganizationRole> {
    try {
      const response = await this.api.get(`/api/v1/organization-roles/${id}`);
      return response.data.data as V2OrganizationRole;
    } catch (error) {
      logger.error("V2 Get Organization Role By ID Error:", error);
      throw error;
    }
  }

  /**
   * Update organization role by ID
   */
  public async updateOrganizationRole(
    id: string,
    payload: { name: string; permissions: string[]; active: boolean }
  ): Promise<V2OrganizationRole> {
    try {
      const response = await this.api.patch(`/api/v1/organization-roles/${id}`, payload);
      return response.data.data as V2OrganizationRole;
    } catch (error) {
      logger.error("V2 Update Organization Role Error:", error);
      throw error;
    }
  }

  /**
   * Get all permissions
   */
  public async getAllPermissions(): Promise<V2Permission[]> {
    try {
      const response = await this.api.get("/api/v1/permissions");
      return response.data.data as V2Permission[];
    } catch (error) {
      logger.error("V2 Get All Permissions Error:", error);
      throw error;
    }
  }

  /**
   * Get a list of organization roles (with optional filters)
   */
  public async getOrganizationRoles(params?: {
    organizationTypeId?: number;
    active?: boolean;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<V2OrganizationRolesResponse> {
    try {
      const response = await this.api.get("/api/v1/organization-roles", {
        params,
      });
      return response.data as V2OrganizationRolesResponse;
    } catch (error) {
      logger.error("V2 Get Organization Roles Error:", error);
      throw error;
    }
  }

  /**
   * Get users by organization type ID, organization ID, and/or role IDs
   * Uses the API's organizationTypeId, organizationId, and organizationRoleId (array) parameters for server-side filtering
   */
  public async getUsersByOrganizationType(
    organizationTypeId: number | null,
    status = "active",
    page = 1,
    limit = 10,
    organizationId?: number | null,
    organizationRoleId?: string[] | null,
    mode?: "ALL" | "CLIENTS" | "PARENT" | "SELF"
  ) {
    try {
      const params = new URLSearchParams();

      if (status) {
        params.append("status", status);
      }

      params.append("page", page.toString());
      params.append("limit", limit.toString());

      // Add organizationTypeId if provided
      if (organizationTypeId !== null && organizationTypeId !== undefined) {
        params.append("organizationTypeId", organizationTypeId.toString());
      }

      // Add organizationId if provided
      if (organizationId !== null && organizationId !== undefined) {
        params.append("organizationId", organizationId.toString());
      }

      // Add mode if provided
      if (mode !== undefined) {
        params.append("mode", mode);
      }

      // Add organizationRoleId if provided (array of role IDs)
      if (organizationRoleId !== null && organizationRoleId !== undefined && organizationRoleId.length > 0) {
        // For array parameters, add each role ID separately or join them
        organizationRoleId.forEach(roleId => {
          params.append("organizationRoleId", roleId);
        });
      }

      const response = await this.api.get(`/api/v1/users?${params.toString()}`);
      logger.log("V2 Users By Organization Type/ID Response:", response);

      return response;
    } catch (error) {
      logger.error("V2 Get Users By Organization Type/ID Error:", error);
      throw error;
    }
  }

  /**
   * Get users by organization role IDs (for filtering employees vs individuals)
   * Note: API doesn't support role filtering, so we fetch all users and filter client-side
   * @deprecated Use getUsersByOrganizationTypes instead
   */
  public async getUsersByRoles(roleIds: string[], status = "active", page = 1, limit = 10) {
    try {
      // Fetch all users (up to a reasonable limit) to ensure we get complete data for filtering
      const params = new URLSearchParams();
      params.append("status", status);
      params.append("page", "1");
      params.append("limit", "1000"); // Get all users in organization

      const response = await this.api.get(`/api/v1/users?${params.toString()}`);
      logger.log("V2 Users By Roles Response:", response);

      // Filter users by role IDs client-side since API doesn't support it
      if (response.data && response.data.data) {
        const allFilteredUsers = response.data.data.filter((user: any) => roleIds.includes(user.organizationRoleId));

        // Implement client-side pagination on filtered results
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedUsers = allFilteredUsers.slice(startIndex, endIndex);

        // Return the same structure but with filtered and paginated data
        return {
          ...response,
          data: {
            ...response.data,
            data: paginatedUsers,
            pagination: {
              totalCount: allFilteredUsers.length,
              totalPages: Math.ceil(allFilteredUsers.length / limit),
              page: page,
              limit: limit,
            },
          },
        };
      }

      return response;
    } catch (error) {
      logger.error("V2 Get Users By Roles Error:", error);
      throw error;
    }
  }

  /**
   * Get self user profile
   */
  public async getSelfUser(): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get("/api/v1/users/me");
      logger.log("V2 Get Self User Response:", response);
      return {
        data: response.data?.data || response.data, // Extract data field from response
        status: response.status,
        message: "User profile fetched successfully",
      };
    } catch (error) {
      logger.error("V2 Get Self User Error:", error);
      throw error;
    }
  }

  /**
   * Update self user password
   * @param payload Password update payload with current and new password
   * @returns Promise with update response
   */
  public async updateSelfPassword(payload: { password: string; newPassword: string }): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.put("/api/v1/users/me/password", payload);
      logger.log("V2 Update Self Password Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Password updated successfully",
      };
    } catch (error) {
      logger.error("V2 Update Self Password Error:", error);
      throw error;
    }
  }

  /**
   * Check if username is available
   * @param username Username to check
   * @returns Promise with availability status
   */
  public async checkUsernameAvailability(username: string): Promise<ApiResponse<{ exists: boolean }>> {
    try {
      const response = await this.api.get(`/api/v1/users/account-check?username=${encodeURIComponent(username)}`);
      logger.log("V2 Username Check Response:", response);
      return {
        data: response.data?.data || response.data,
        status: response.status,
        message: "Username availability checked",
      };
    } catch (error) {
      logger.error("V2 Username Check Error:", error);
      throw error;
    }
  }

  /**
   * Get user by ID
   * @param userId User ID to fetch
   * @returns Promise with user data
   */
  public async getUserById(userId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get(`/api/v1/users/${userId}`);
      logger.log("V2 Get User By ID Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "User fetched successfully",
      };
    } catch (error) {
      logger.error("V2 Get User By ID Error:", error);
      throw error;
    }
  }

  /**
   * Delete user by ID
   * @param userId User ID to delete
   * @returns Promise with delete response
   */
  public async deleteUser(userId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.delete(`/api/v1/users/${userId}`);
      logger.log("V2 Delete User Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "User deleted successfully",
      };
    } catch (error) {
      logger.error("V2 Delete User Error:", error);
      throw error;
    }
  }

  /**
   * Update self user profile
   */
  public async updateSelfUser(payload: V2UpdateSelfUserRequest): Promise<V2SelfUserResponse> {
    try {
      const response = await this.api.patch("/api/v1/users/me", payload);
      return response.data as V2SelfUserResponse;
    } catch (error) {
      logger.error("V2 Update Self User Error:", error);
      throw error;
    }
  }

  /**
   * Delete self user account
   * @returns Promise with delete response (204 No Content on success)
   */
  public async deleteUserAccount(): Promise<ApiResponse<void>> {
    try {
      const response = await this.api.delete("/api/v1/users/me");
      logger.log("V2 Delete User Account Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Account deleted successfully",
      };
    } catch (error) {
      logger.error("V2 Delete User Account Error:", error);
      throw error;
    }
  }

  /**
   * Individual subscriber self sign-up using V2 API (no auth required)
   * @param payload Individual subscriber signup request data
   * @returns Promise with signup response
   */
  public async individualSubscriberSignup(payload: V2IndividualSubscriberSignupRequest): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post("/api/v1/users/subscriber-signup", payload);
      logger.log("V2 Individual Subscriber Signup Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Individual subscriber signup successful",
      };
    } catch (error) {
      logger.error("V2 Individual Subscriber Signup Error:", error);
      throw error;
    }
  }

  /**
   * Get default password policy (no auth required)
   * @returns Promise with password policy response
   */
  public async getDefaultPasswordPolicy(): Promise<ApiResponse<any>> {
    try {
      const response = await axios.get(`${Constants.USER_API_BASE_URL}/auth/v1/password-policies/default`, {
        headers: { "Content-Type": "application/json" },
      });
      logger.log("V2 Get Default Password Policy Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Password policy fetched successfully",
      };
    } catch (error) {
      logger.error("V2 Get Default Password Policy Error:", error);
      throw error;
    }
  }

  /**
   * Initiate forgot password flow (no auth required)
   * @param payload Forgot password request with username and confirmationUrl
   * @returns Promise with accepted response
   */
  public async forgotPassword(payload: V2ForgotPasswordRequest): Promise<ApiResponse<void>> {
    try {
      const response = await axios.post(`${Constants.USER_API_BASE_URL}/auth/v1/forgotpassword`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      logger.log("V2 Forgot Password Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Password reset email sent successfully",
      };
    } catch (error) {
      logger.error("V2 Forgot Password Error:", error);
      throw error;
    }
  }

  /**
   * Reset password with token (no auth required)
   * @param payload Reset password request with token and new password
   * @returns Promise with login response containing tokens and user
   */
  public async resetPasswordWithToken(payload: V2ResetPasswordRequest): Promise<ApiResponse<V2ResetPasswordResponse>> {
    try {
      const response = await axios.post(`${Constants.USER_API_BASE_URL}/auth/v1/password`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      logger.log("V2 Reset Password Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Password reset successfully",
      };
    } catch (error) {
      logger.error("V2 Reset Password Error:", error);
      throw error;
    }
  }

  /**
   * Confirm email change with verification code
   * @param payload Payload containing the verification code
   * @returns Promise with confirmation response
   */
  public async confirmEmailChange(payload: { code: string }): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(`${Constants.USER_API_BASE_URL}/auth/v1/confirm/email`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("access_token")}`,
        },
      });
      logger.log("V2 Confirm Email Change Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: response.data?.message || "Email verified successfully",
      };
    } catch (error) {
      logger.error("V2 Confirm Email Change Error:", error);
      throw error;
    }
  }

  /**
   * Request email change confirmation link
   * @param payload Payload containing new email and confirmation URL
   * @returns Promise with confirmation response
   */
  public async requestEmailChange(payload: {
    emailAddress: string;
    confirmationUrl: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(`${Constants.USER_API_BASE_URL}/auth/v1/confirm/email`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("access_token")}`,
        },
      });
      logger.log("V2 Request Email Change Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: response.data?.message || "Confirmation email sent",
      };
    } catch (error) {
      logger.error("V2 Request Email Change Error:", error);
      throw error;
    }
  }

  /**
   * Rotate password after expiry per policy (no auth required)
   * @param payload Password rotation request with username, current password, and new password
   * @returns Promise with login response containing tokens and user
   */
  public async rotatePassword(payload: {
    username: string;
    password: string;
    newPassword: string;
  }): Promise<ApiResponse<V2ResetPasswordResponse>> {
    try {
      const response = await axios.post(`${Constants.USER_API_BASE_URL}/auth/v1/password/rotation`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      logger.log("V2 Password Rotation Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Password rotated successfully",
      };
    } catch (error) {
      logger.error("V2 Password Rotation Error:", error);
      throw error;
    }
  }
}

// Organization API Service - uses /org/ base URL
class OrganizationApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    // Create authenticated API instance for Organization endpoints
    this.api = axios.create({
      baseURL: `${Constants.ORG_API_BASE_URL}/`,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token interceptor
    this.api.interceptors.request.use(config => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor with refresh token logic
    this.api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, add to queue
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.api(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Attempt to refresh token
            const refreshToken = localStorage.getItem("refresh_token");
            if (!refreshToken) {
              throw new Error("No refresh token available");
            }

            const response = await this.refreshToken(refreshToken);

            // Update tokens in localStorage
            localStorage.setItem("access_token", response.access_token);
            localStorage.setItem("refresh_token", response.refresh_token);

            // Update the failed queue
            this.processQueue(null, response.access_token);

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            this.processQueue(refreshError, null);
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("authToken");
            localStorage.removeItem("currentUser");

            // Redirect to signin page only if not on public pages
            const publicPages = [
              "/home",
              "/signin",
              "/auth/signin",
              "/signup",
              "/host-experience",
              "/report-issue",
              "/corporate-page",
              "/terms-of-use",
              "/privacy-policy",
            ];
            if (!publicPages.includes(window.location.pathname)) {
              window.location.href = "/signin";
            }
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Process the queue of failed requests
   */
  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  /**
   * Refresh access token using refresh token
   * @param refreshToken The refresh token to use
   * @returns Promise with new access and refresh tokens
   */
  public async refreshToken(refreshToken: string): Promise<V2RefreshTokenResponse> {
    try {
      const response = await axios.post(
        `${Constants.USER_API_BASE_URL}/auth/v1/token/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      logger.log("Token refresh successful");
      return response.data;
    } catch (error) {
      logger.error("Token refresh failed:", error);
      throw error;
    }
  }

  /**
   * Get a list of organizations matching query
   * @param params Query parameters for filtering organizations
   */
  public async getOrganizations(
    params: {
      countryId?: number;
      typeId?: number;
      parentId?: number;
      projectId?: number;
      groupId?: number;
      status?: "INACTIVE" | "ACTIVE" | "SUSPENDED" | "DISABLED";
      spaceType?: "LOW" | "MEDIUM" | "HIGH";
      page?: number;
      limit?: number;
      orderBy?: "name:asc" | "name:desc" | "createdAt:asc" | "createdAt:desc";
    } = {}
  ) {
    try {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });

      const queryString = searchParams.toString();
      const url = `api/v1/organizations${queryString ? `?${queryString}` : ""}`;

      const response = await this.api.get(url);
      logger.log("Organization Get Organizations Response:", response);
      return response;
    } catch (error) {
      logger.error("Organization Get Organizations Error:", error);
      throw error;
    }
  }

  /**
   * Get organization by ID
   * @param organizationId Organization ID to fetch
   */
  public async getOrganizationById(organizationId: number) {
    try {
      logger.log("API: Making request to /api/v1/organizations/" + organizationId);
      const response = await this.api.get(`/api/v1/organizations/${organizationId}`);
      logger.log("API: Organization response received:", response);
      logger.log("Organization Get Organization Response:", response);
      return response;
    } catch (error) {
      logger.error("API: Organization request failed:", error);
      logger.error("Organization Get Organization Error:", error);
      throw error;
    }
  }

  /**
   * Update self organization
   * @param payload Organization update payload
   * @returns Promise with update response
   */
  public async updateSelfOrganization(payload: {
    name?: string;
    code?: string;
    contactEmail?: string;
    alternateEmail?: string;
    contactMsisdn?: string;
    address?: string;
    city?: string;
    region?: string;
    logoUrl?: string;
    domain?: string;
    webUrl?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.patch("/api/v1/organizations/me", payload);
      logger.log("V2 Update Self Organization Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Organization updated successfully",
      };
    } catch (error) {
      logger.error("V2 Update Self Organization Error:", error);
      throw error;
    }
  }

  /**
   * Search for organizations
   * @param params Search parameters for finding organizations
   */
  public async searchOrganizations(params: {
    q: string;
    parentId?: number;
    page?: number;
    limit?: number;
    status?: string;
    typeId?: number;
    orderBy?: string;
  }) {
    try {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });

      const queryString = searchParams.toString();
      const url = `/api/v1/organizations/search${queryString ? `?${queryString}` : ""}`;
      logger.log("🚀 ApiV2Service.searchOrganizations calling URL:", url);

      const response = await this.api.get(url);
      logger.log("📦 ApiV2Service.searchOrganizations response received:", response);
      logger.log("Organization Search Organizations Response:", response);
      return response;
    } catch (error) {
      logger.error("❌ ApiV2Service.searchOrganizations Error:", error);
      logger.error("Organization Search Organizations Error:", error);
      throw error;
    }
  }

  /**
   * Update a client organization
   * @param organizationId Organization ID to update
   * @param payload V2UpdateClientOrganizationRequest
   */
  public async updateClientOrganization(organizationId: number, payload: V2UpdateClientOrganizationRequest) {
    try {
      const response = await this.api.patch(`api/v1/organizations/me/client-orgs/${organizationId}`, payload);
      logger.log("Organization Update Client Organization Response:", response);
      return response;
    } catch (error) {
      logger.error("Organization Update Client Organization Error:", error);
      throw error;
    }
  }

  /**
   * Delete (disable) a client organization
   * @param organizationId Client Organization ID to delete
   */
  public async deleteClientOrganization(organizationId: number) {
    try {
      const response = await this.api.delete(`api/v1/organizations/me/client-orgs/${organizationId}`);
      logger.log("Organization Delete Client Organization Response:", response);
      return response;
    } catch (error) {
      logger.error("Organization Delete Client Organization Error:", error);
      throw error;
    }
  }

  /**
   * Update an organization's approved status
   * @param organizationId Organization ID to update
   * @param payload Object containing approved boolean state
   */
  public async updateApprovedStatus(organizationId: number, payload: { approved: boolean }) {
    try {
      const response = await this.api.put(`/api/v1/organizations/${organizationId}/approved`, payload);
      logger.log("Organization Update Approved Status Response:", response);
      return response;
    } catch (error) {
      logger.error("Organization Update Approved Status Error:", error);
      throw error;
    }
  }

  /**
   * Update an organization directly
   * @param organizationId Organization ID to update
   * @param payload Organization update payload
   */
  public async updateOrganization(
    organizationId: number,
    payload: {
      name?: string;
      code?: string;
      contactEmail?: string;
      alternateEmail?: string;
      contactMsisdn?: string;
      address?: string;
      city?: string;
      region?: string;
      logoUrl?: string;
      domain?: string;
      webUrl?: string;
      status?: "ACTIVE" | "INACTIVE" | "DISABLED" | "SUSPENDED";
      statusReason?: string;
      countryId?: number;
      typeId?: number;
      projectId?: number;
      groupId?: number;
      spaceType?: "LOW" | "MEDIUM" | "HIGH";
    }
  ) {
    try {
      const response = await this.api.patch(`api/v1/organizations/${organizationId}`, payload);
      logger.log("Organization Update Response:", response);
      return response;
    } catch (error) {
      logger.error("Organization Update Error:", error);
      throw error;
    }
  }

  /**
   * Update an organization's country
   * @param organizationId Organization ID to update
   * @param countryId New country ID
   * @returns Promise with updated organization response
   */
  public async updateOrganizationCountry(organizationId: number, countryId: number): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.put(`api/v1/organizations/${organizationId}/country`, {
        countryId,
      });
      logger.log("Organization Update Country Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Organization country updated successfully",
      };
    } catch (error) {
      logger.error("Organization Update Country Error:", error);
      throw error;
    }
  }

  /**
   * Create a property management client organization
   * @param organizationId Parent Organization ID
   * @param payload V2CreateClientOrganizationRequest
   */
  public async createPropertyManagementClientOrganization(
    organizationId: number,
    payload: V2CreateClientOrganizationRequest
  ) {
    try {
      const response = await this.api.post(
        `api/v1/organizations/${organizationId}/type/property-management/client-orgs`,
        payload
      );
      logger.log("Organization Create Property Management Client Organization Response:", response);
      return response;
    } catch (error) {
      logger.error("Organization Create Property Management Client Organization Error:", error);
      throw error;
    }
  }

  /**
   * Get organization types using authenticated API
   * @returns Promise with organization types response
   */
  public async getOrganizationTypes(): Promise<ApiResponse<V2OrganizationTypesResponse>> {
    try {
      const response = await this.api.get("/api/v1/organization-types");
      logger.log("Organization Get Organization Types Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: "Organization types fetched successfully",
      };
    } catch (error) {
      logger.error("Organization Get Organization Types Error:", error);
      throw error;
    }
  }

  /**
   * Self-signup using V2 API (no auth required)
   * @param signupData Signup request data
   * @returns Promise with signup response
   */
  public async selfSignup(signupData: V2SignupRequest): Promise<ApiResponse<V2SignupResponse>> {
    try {
      const response = await this.api.post("/api/v1/organizations/self-signup", signupData);

      logger.log("V2 Self Signup Response:", response);
      return {
        data: response.data,
        status: response.status,
        message: response.data?.message || "Signup successful",
      };
    } catch (error) {
      logger.error("V2 Self Signup Error:", error);
      throw error;
    }
  }
}

// New V2PublicApiService class for public endpoints
class V2PublicApiService {
  private api: AxiosInstance;

  constructor(baseURL: string = Constants.ORG_API_BASE_URL) {
    // Create public API instance for V2 endpoints (no auth required)
    this.api = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add response interceptor
    this.api.interceptors.response.use(
      response => ({
        data: response.data,
        status: response.status,
        message: response.data?.message,
      }),
      error => {
        logger.error("V2 Public API Error:", error);
        throw error;
      }
    );
  }

  /**
   * Get countries list using V2 API (no auth required)
   * @returns Promise with countries list
   */
  public async getCountries(): Promise<ApiResponse<V2CountriesResponse>> {
    try {
      const response = await this.api.get("/api/v1/countries?orderBy=name:asc");

      logger.log("V2 Countries Response:", response);
      return {
        data: { data: response.data.data }, // Wrap to match expected V2CountriesResponse structure
        status: response.status,
        message: "Countries fetched successfully",
      };
    } catch (error) {
      logger.error("V2 Get Countries Error:", error);
      throw error;
    }
  }

  /**
   * Get organization types using V2 API (no auth required)
   * @returns Promise with organization types
   */
  public async getOrganizationTypes(): Promise<ApiResponse<V2OrganizationTypesResponse>> {
    try {
      const response = await this.api.get("/api/v1/organization-types/registry");
      logger.log("V2 Organization Types Response:", response);
      return {
        data: response.data, // Return the full response including data and pagination
        status: response.status,
        message: "Organization types fetched successfully",
      };
    } catch (error) {
      logger.error("V2 Get Organization Types Error:", error);
      throw error;
    }
  }

  /**
   * Check if organization code and/or name exists
   * @param code - Organization code to check
   * @param name - Organization name to check
   * @returns Promise with validation result
   */
  public async checkOrganizationExists(params: {
    code?: string;
    name?: string;
  }): Promise<ApiResponse<{ codeExists: boolean | null; nameExists: boolean | null }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.code) queryParams.append("code", params.code);
      if (params.name) queryParams.append("name", params.name);

      const response = await this.api.get(`/api/v1/organizations/check?${queryParams.toString()}`);
      logger.log("V2 Check Organization Response:", response);
      return {
        data: response.data.data,
        status: response.status,
        message: "Organization check completed successfully",
      };
    } catch (error) {
      logger.error("V2 Check Organization Error:", error);
      throw error;
    }
  }
}

export const apiV2Service = new ApiV2Service();
export const organizationApiService = new OrganizationApiService();
export const v2PublicApiService = new V2PublicApiService();
export default apiV2Service;
